import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import InputAutosize from 'react-input-autosize';

import type { InferPropTypes } from '@agilemd/ui-utils';
import { addVariants, useOverriddenState, useQuickValue } from '@agilemd/ui-utils';

import styles from './styles.module.scss';

export const VARIANTS = ['default'] as const;
const Input = InputAutosize;
type InlineTextEditorVariant = (typeof VARIANTS)[number];
const propTypes = {
  /**
   * The current value to display
   */
  value: PropTypes.string,
  /**
   * The initial value to display
   */
  initialValue: PropTypes.string,
  /**
   * Fires `onChange` of the current value
   */
  onChange: PropTypes.func,
  /**
   * Whether the input should be disabled
   */
  isEditing: PropTypes.bool,
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Decides the variant
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf<InlineTextEditorVariant>(VARIANTS).isRequired,
    PropTypes.bool.isRequired,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf<InlineTextEditorVariant>(VARIANTS).isRequired,
        PropTypes.bool.isRequired
      ]).isRequired
    )
  ]),
  minWidth: PropTypes.number,
  placeholder: PropTypes.string
};

export interface InlineTextEditorOverrideProps {
  onChange: (value: string) => void;
}

export type InlineTextEditorProps = InferPropTypes<
  typeof propTypes,
  never,
  InlineTextEditorOverrideProps
>;

/**
 * An InlineTextEditor is a text input that is displayed as though it were normal text but when hovered can be edited.
 */
const InlineTextEditor: React.FC<InlineTextEditorProps> = ({
  initialValue = '',
  value: valueFromProps = '',
  onChange = () => undefined,
  className = '',
  variant = 'default',
  isEditing = true,
  minWidth = undefined,
  placeholder = undefined
}: InlineTextEditorProps) => {
  const [text, setText] = useOverriddenState(initialValue, valueFromProps, {
    onChange
  });
  // Needed to respond to onChange event synchronously for performance reasons
  const [inputValue, onChangeInput] = useQuickValue(text, e => setText(e.target.value));
  const Display = isEditing
    ? Input
    : ({
        value,
        inputClassName
      }: {
        value: string;

        inputClassName: string;
      }) => <div className={inputClassName}>{value}</div>;
  return (
    // @ts-ignore
    <Display
      inputClassName={classnames(
        addVariants('aglmd-inline-text-editor', styles, isEditing ? 'editing' : 'static', variant),
        className
      )}
      placeholder={placeholder}
      value={inputValue}
      minWidth={minWidth}
      onChange={onChangeInput}
    />
  );
};

InlineTextEditor.propTypes = propTypes;
export default InlineTextEditor;
